import { inject } from "@angular/core";
import { Auth, user } from "@angular/fire/auth";
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from "@angular/router";
import { EventDataService } from 'nexus-core';
import { catchError, combineLatest, firstValueFrom, map, Observable, of, switchMap } from "rxjs";
import { AuthzService } from "./authz.service";

export const hasPermission: (permission?: string) => CanActivateFn = (permission?: string) =>
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const authzService = inject(AuthzService);
    const eventDataService = inject(EventDataService);
    const eventKey = route.paramMap.get('eventKey');

    if(!eventKey) return false;

    return authzService.isAuthorized(eventKey, permission).pipe(
      switchMap(async isAuthorized => {
        if (isAuthorized) return true;

        const name = await firstValueFrom(eventDataService.getEventName(eventKey));
        if(!name) {
          console.warn('Event not found', eventKey);
          return router.parseUrl(`/error?message=Event not found&url=${getResolvedUrl(route)}`);
        }

        return router.parseUrl(`/login?event=${eventKey}&permission=${permission || ''}&url=${getResolvedUrl(route)}`);
      }),
      catchError(() => {
        return of(router.parseUrl(`/error?url=${getResolvedUrl(route)}`));
      }),
    );
  };

export const multiHasPermission: (permission?: string) => CanActivateFn = (permission?: string) =>
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const authzService = inject(AuthzService);
    const eventDataService = inject(EventDataService);
    const eventKeys = route.queryParamMap.get('events')?.split(',');

    if(!eventKeys) return router.parseUrl(`/error?message=Events parameter required&url=${getResolvedUrl(route)}`);;

    const requests: {[eventKey: string]: Observable<boolean>} = {};
    for(const eventKey of eventKeys) {
      requests[eventKey] = authzService.isAuthorized(eventKey, permission);
    }

    return combineLatest(requests).pipe(
      switchMap(async isAuthorized => {
        for(const [eventKey, a] of Object.entries(isAuthorized)) {
          if(!a) {
            const name = await firstValueFrom(eventDataService.getEventName(eventKey));
            if(!name) {
              console.warn('Event not found', eventKey);
              return router.parseUrl(`/error?message=Event not found&url=${getResolvedUrl(route)}&details=${eventKey}`);
            }

            return router.parseUrl(`/login?event=${eventKey}&permission=${permission || ''}&url=${getResolvedUrl(route)}`);
          }
        }

        return true;
      }),
      catchError(() => {
        return of(router.parseUrl(`/error?url=${getResolvedUrl(route)}`));
      }),
    );
  };

function getResolvedUrl(route: ActivatedRouteSnapshot): string {
  const query = route.queryParams ? '?' + Object.entries(route.queryParams).map(([k,v]) => `${k}=${v}`).join('&') : '';
  return encodeURIComponent(route.pathFromRoot
    .map(v => v.url.map(segment => segment.toString()).join('/'))
    .join('/') + query);
}

export const isLoggedIn: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const auth = inject(Auth);
    const router = inject(Router);

    return user(auth).pipe(
      map(u => {
        if(u) return true;

        return router.parseUrl(`/login?redirect=${encodeURIComponent(state.url)}`);
      }),
    );
  };

export const isSuperAdmin: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const db = inject(AuthzService);

    return db.isSuperAdmin();
  };

export const isLoggedInNonAnon: CanActivateFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const auth = inject(Auth);
    const router = inject(Router);

    return user(auth).pipe(
      map(u => {
        if(u && !u.isAnonymous) return true;

        return router.parseUrl(`/login?redirect=${encodeURIComponent(state.url)}&nonAnon=true`);
      }),
    );
  };
